import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Mountain, ArrowRight, ChevronRight, MapPin, BookOpen, Layers } from "lucide-react";
import api, { resolveImg } from "../lib/api";
import Seo from "../components/Seo";
import { SITE_URL, BUSINESS } from "../lib/site";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    api.get(`/blog/${slug}`)
      .then((r) => { setPost(r.data); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  if (status === "loading") return <div className="min-h-screen flex items-center justify-center bg-ivory text-fir-deep/60">Caricamento…</div>;

  if (status === "notfound") return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-ivory text-center px-6">
      <Seo title="Guida non trovata" path={`/guide/${slug}`} noindex />
      <h1 className="font-display text-4xl text-fir">Guida non trovata</h1>
      <p className="text-fir-deep/60 mt-3">L'articolo che cerchi non esiste o è stato rimosso.</p>
      <Link to="/guide" className="gold-btn px-6 py-3 rounded-xl mt-6 inline-flex items-center gap-2">Tutte le guide <ArrowRight size={16} /></Link>
    </div>
  );

  const url = `${SITE_URL}/guide/${post.slug}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": post.is_pillar ? "Article" : "BlogPosting",
        headline: post.title,
        description: post.meta_description || post.excerpt,
        inLanguage: "it-IT",
        image: post.cover_image ? [resolveImg(post.cover_image)] : undefined,
        datePublished: post.published_at || post.created_at,
        dateModified: post.updated_at || post.created_at,
        author: { "@type": "Organization", name: BUSINESS.name },
        publisher: { "@type": "Organization", name: BUSINESS.name, url: SITE_URL },
        mainEntityOfPage: url,
        keywords: (post.keywords || []).join(", "),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
          { "@type": "ListItem", position: 2, name: "Guide", item: `${SITE_URL}/guide` },
          { "@type": "ListItem", position: 3, name: post.title, item: url },
        ],
      },
    ],
  };

  return (
    <div className="bg-ivory min-h-screen">
      <Seo title={post.title} description={post.meta_description || post.excerpt} path={`/guide/${post.slug}`} image={post.cover_image ? resolveImg(post.cover_image) : undefined} jsonLd={jsonLd} />

      <header className="sticky top-0 z-30 bg-fir-deep/95 backdrop-blur text-ivory px-6 md:px-16 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2"><Mountain className="text-gold" size={24} /><span className="font-display text-xl">Appartamenti in Val di Sole</span></Link>
        <Link to="/#strutture" className="gold-btn px-4 py-2 rounded-xl text-sm">Verifica disponibilità</Link>
      </header>

      <article className="max-w-3xl mx-auto px-6 pt-12 pb-20">
        <nav className="flex items-center gap-1.5 text-sm text-fir-deep/60 mb-6" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-gold">Home</Link><ChevronRight size={14} />
          <Link to="/guide" className="hover:text-gold">Guide</Link><ChevronRight size={14} />
          <span className="text-fir-deep/80 truncate">{post.title}</span>
        </nav>

        {post.is_pillar && (
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wide bg-gold/15 text-gold px-3 py-1 rounded-full mb-3"><Layers size={13} /> Guida completa</span>
        )}
        <h1 className="font-display text-4xl sm:text-5xl text-fir leading-tight">{post.title}</h1>
        {post.created_at && <p className="text-fir-deep/50 text-sm mt-3">{new Date(post.published_at || post.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</p>}

        {post.pillar && (
          <Link to={`/guide/${post.pillar.slug}`} data-testid="blog-pillar-backlink" className="mt-5 inline-flex items-center gap-2 text-sm bg-stone/60 hover:bg-stone rounded-full px-4 py-2 text-fir transition-colors">
            <BookOpen size={15} className="text-gold" /> Fa parte di: <strong>{post.pillar.title}</strong>
          </Link>
        )}

        {post.cover_image && (
          <img src={resolveImg(post.cover_image)} alt={post.title} data-testid="blog-cover" className="w-full rounded-3xl mt-8 object-cover max-h-[460px]" />
        )}

        <div className="blog-content mt-8" data-testid="blog-content" dangerouslySetInnerHTML={{ __html: post.content_html }} />

        {post.is_pillar && post.children?.length > 0 && (
          <section className="mt-14" data-testid="blog-cluster">
            <h2 className="font-display text-3xl text-fir mb-6">Approfondimenti della guida</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {post.children.map((ch) => (
                <Link key={ch.id} to={`/guide/${ch.slug}`} data-testid="blog-cluster-card" className="group bg-white rounded-2xl border border-stone overflow-hidden hover:shadow-lg hover:border-gold/40 transition-all flex flex-col">
                  {ch.cover_image && <img src={resolveImg(ch.cover_image)} alt={ch.title} loading="lazy" className="h-40 w-full object-cover" />}
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="font-display text-xl text-fir group-hover:text-gold transition-colors leading-snug">{ch.title}</h3>
                    <p className="text-fir-deep/70 text-sm mt-2 flex-1">{ch.excerpt || ch.meta_description}</p>
                    <span className="mt-3 text-gold text-sm inline-flex items-center gap-1">Leggi <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-12 bg-fir-deep text-ivory rounded-3xl p-8 text-center">
          <h2 className="font-display text-2xl text-gold">Pronto a partire per la Val di Sole?</h2>
          <p className="text-ivory/70 mt-2">Verifica la disponibilità delle nostre strutture a Dimaro e prenota diretto.</p>
          <Link to="/#strutture" className="gold-btn px-7 py-3 rounded-xl mt-5 inline-flex items-center gap-2">Verifica disponibilità <ArrowRight size={16} /></Link>
        </div>

        <Link to="/guide" className="mt-8 inline-flex items-center gap-1.5 text-fir hover:text-gold text-sm">← Tutte le guide</Link>
      </article>

      <footer className="bg-fir-deep text-ivory px-6 py-10">
        <div className="max-w-3xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2"><Mountain className="text-gold" size={24} /><span className="font-display text-2xl">Appartamenti in Val di Sole</span></div>
          <div className="text-ivory/60 text-sm flex items-center gap-2"><MapPin size={15} className="text-gold" /> {BUSINESS.locality}, Val di Sole (TN) · {BUSINESS.phone}</div>
        </div>
        <div className="max-w-3xl mx-auto mt-4 flex gap-4 text-sm">
          <Link to="/guide" className="text-ivory/50 hover:text-gold">Guide</Link>
          <Link to="/privacy" className="text-ivory/50 hover:text-gold">Privacy</Link>
          <Link to="/cookie-policy" className="text-ivory/50 hover:text-gold">Cookie Policy</Link>
        </div>
      </footer>
    </div>
  );
}
